import { memo, useCallback } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { QuantitySelector } from 'pages/sales/modules/components';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { SText } from '@setvi/shared/components';
import { FirstRowBox, RowContainer, SecondRowBox } from '../styled';

interface QuantityColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onQuantityChange: (productId: string, value: number) => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const useStyles = makeStyles(() => ({
  textFieldClassName: {
    '& input': {
      padding: '8px 12px'
    }
  }
}));

export const QuantityColumn = memo(
  ({
    product,
    productId,
    onQuantityChange,
    disabled = false,
    disabledTooltip = ''
  }: QuantityColumnProps) => {
    const classes = useStyles();

    const step =
      product?.overrideMinimumQuoteQuantity === false
        ? product?.minimumQuoteQuantity || 1
        : 1;

    const handleQuantityChange = useCallback(
      (value: number) => {
        if (!disabled) {
          onQuantityChange(productId, value);
        }
      },
      [disabled, onQuantityChange, productId]
    );

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Skeleton variant="rect" width={130} height={40} />
          </FirstRowBox>
          <SecondRowBox>
            {!!product.unitOfMeasure && (
              <Skeleton variant="rect" width={100} height={30} />
            )}
          </SecondRowBox>
        </RowContainer>
      );
    }

    const quantityInput = (
      <Box maxWidth={160}>
        <QuantitySelector
          quantity={product?.quantity}
          min={step}
          step={step}
          onInputChange={handleQuantityChange}
          textFieldClassName={classes.textFieldClassName}
          disabled={disabled}
        />
      </Box>
    );

    return (
      <RowContainer>
        <FirstRowBox>
          {disabled && disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow>
              <Box>{quantityInput}</Box>
            </Tooltip>
          ) : (
            quantityInput
          )}
        </FirstRowBox>
        <SecondRowBox>
          {!!product.unitOfMeasure && (
            <SText
              title={`UOM: ${product.unitOfMeasure}`}
              style={{
                width: '100%',
                textAlign: 'center'
              }}
            />
          )}
        </SecondRowBox>
      </RowContainer>
    );
  }
);

QuantityColumn.displayName = 'QuantityColumn';
